import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  StackLayout,
  StackItem,
  GDSSpaceToken,
  spacingTokenMap,
  useMediaQuery,
  queryHelpers,
  Box,
} from '@leagueplatform/genesis-core';
import {
  MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

export interface MasonryStickyLayoutNodeProperties {
  spacing?: GDSSpaceToken;
}

export type MasonryStickyLayoutNodeActions = {};

export type MasonryStickyLayoutNode = MasonryEngineNode<
  'stickyLayout',
  MasonryStickyLayoutNodeProperties,
  MasonryStickyLayoutNodeActions,
  {
    items: MasonryEngineNode[];
    headerItems?: MasonryEngineNode[];
    footerItems?: MasonryEngineNode[];
  }
>;

export type MasonryStickyLayoutRendererProps =
  MasonryEngineNodeRendererProps<MasonryStickyLayoutNode>;

export const MasonryStickyLayoutRenderer = ({
  items,
  headerItems,
  footerItems,
  spacing = 'one',
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryStickyLayoutRendererProps) => {
  // disables sticky behaviour for header / footer when mobile landscape. Prepends header and appends footer to items

  const isMobileLandscape = useMediaQuery(queryHelpers.only('mobileLandscape'));
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [footerHeight, setFooterHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }

    if (footerRef.current) {
      setFooterHeight(footerRef.current.offsetHeight);
    }
  }, []);

  if (!items) return null;

  const layoutSpacing = spacingTokenMap[spacing] || '$one';

  // Calculate bottom padding based on viewport
  const getBottomPadding = () => {
    if (isMobileLandscape) return '$none';
    if (isMobile)
      return `calc(${footerHeight}px + ${layoutSpacing} + ${layoutSpacing} + ${layoutSpacing})`;
    return `calc(${footerHeight}px + ${layoutSpacing})`;
  };

  return (
    <Box
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...css,
      }}
    >
      <StackLayout
        spacing={isMobileLandscape ? layoutSpacing : '$none'}
        horizontalAlignment="stretch"
        orientation="vertical"
        css={{
          flex: 1,
          overflowY: isMobileLandscape ? 'visible' : 'auto',
          paddingTop: isMobileLandscape
            ? '$none'
            : `calc(${headerHeight}px + ${layoutSpacing})`,
          paddingBottom: getBottomPadding(),
        }}
      >
        {headerItems && (
          <Box
            css={{
              position: isMobileLandscape ? 'static' : 'absolute',
              top: 0,
              left: 0,
              zIndex: '$default',
              width: '100%',
            }}
          >
            <StackLayout
              ref={headerRef}
              horizontalAlignment="stretch"
              orientation="vertical"
              spacing={layoutSpacing}
            >
              {React.Children.map(
                headerItems,
                (item) => item && <StackItem>{item}</StackItem>,
              )}
            </StackLayout>
          </Box>
        )}
        {items && (
          <StackLayout
            horizontalAlignment="stretch"
            orientation="vertical"
            spacing={layoutSpacing}
            css={{
              height: 'fit-content',
              paddingLeft: isMobile ? '$oneAndHalf' : '$none',
              paddingRight: isMobile ? '$oneAndHalf' : '$none',
            }}
          >
            {React.Children.map(
              items,
              (item) => item && <StackItem>{item}</StackItem>,
            )}
          </StackLayout>
        )}
      </StackLayout>

      {footerItems && (
        <Box
          css={{
            position: isMobileLandscape ? 'static' : 'sticky',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: '$high',
          }}
        >
          <StackLayout
            ref={footerRef}
            horizontalAlignment="stretch"
            orientation="vertical"
            spacing={layoutSpacing}
          >
            {React.Children.map(
              footerItems,
              (item) => item && <StackItem>{item}</StackItem>,
            )}
          </StackLayout>
        </Box>
      )}
    </Box>
  );
};
